import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  TablePagination,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  TableSortLabel,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import {
  downloadFileByKey,
  RetryPolicyPDFUpload,
  RetryFailedPolicy,
  RetryBulkPdfUpload,
} from "../../../../Service/_index";
import CommonLoader from "../../../../UiComponents/CommonLoader/CommonLoader";
import {
  ToastError,
  ToastSuccess,
} from "../../../../UiComponents/Toaster/Toast";

// Company-specific required fields configuration
const COMPANY_REQUIRED_FIELDS = {
  reliance: [
    "policyNumber",
    "customerName",
    "make",
    "fuelType",
    "model",
    "registrationNumber",
    "registrationYear",
    "odDisc",
    "issueDate",
    "odPremium",
    "seatingCapacity",
    "addOnPremium",
    "loginIdFull",
    "tpPremium",
    "totalPremium",
    "cc",
  ],
  united: [
    "policyNumber",
    "registrationYear",
    "make",
    "customerName",
    "mobileNumber",
    "model",
    "odPremium",
    "tpPremium",
    "totalPremium",
    "gvw",
    "loginIdFull",
  ],
  national: [
    "customerName",
    "policyNumber",
    "registrationNumber",
    "make",
    "model",
    "seatingCapacity",
    "fuelType",
    "registrationYear",
    "cc",
    "odPremium",
    "tpPremium",
    "premium",
    "pospCode",
  ],
};

const getOriginalFileName = (fileName, originalFileName) => {
  // If originalFileName is provided from backend, use it
  if (originalFileName) {
    return originalFileName;
  }
  // Otherwise, fallback to parsing the fileName
  if (!fileName) return "";
  const parts = fileName.split("-");
  if (parts.length > 2) {
    return parts.slice(2).join("-");
  }
  return fileName;
};


const BulkUploadedPolicyTable = ({
  bulkUploadedData,
  onDownloadMissingFields,
  onImportClick,
  expandedRows,
  onExpandRow,
  showMatched,
  showUnmatched,
  onToggleShowMatched,
  onToggleShowUnmatched,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  loading,
  onRefresh,
}) => {
  // State for Excel Files dropdown menu (track which row's menu is open)
  const [filesMenuAnchor, setFilesMenuAnchor] = useState(null);
  const [selectedFileGroupId, setSelectedFileGroupId] = useState(null);
  const [retryingPolicies, setRetryingPolicies] = useState(new Set());

  // State for screenshot preview dialog
  const [screenshotDialogOpen, setScreenshotDialogOpen] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  const handleFilesMenuClick = (event, bulkUploadFileId) => {
    setFilesMenuAnchor(event.currentTarget);
    setSelectedFileGroupId(bulkUploadFileId);
  };

  const handleFilesMenuClose = () => {
    setFilesMenuAnchor(null);
    setSelectedFileGroupId(null);
  };

  const handleDownloadFileByKey = async (fileKey, fileName) => {
    if (!fileKey) {
      ToastError("File key not available");
      return;
    }
    try {
      const res = await downloadFileByKey(fileKey);
      if (res?.data?.downloadURL) {
        window.open(res.data.downloadURL, "_blank");
      } else {
        ToastError("Failed to get download URL");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      ToastError(`Failed to download ${fileName}`);
    }
    handleFilesMenuClose();
  };

  /**
   * Handle retry PDF upload for all failed policies in a bulk upload file
   */
  const handleRetryPDFUpload = async (group) => {
    const failedCount = group.pdfStats?.failed || 0;

    if (failedCount === 0) {
      ToastError("No failed PDFs to retry");
      return;
    }

    try {
      // Add to retrying set to show loading state
      setRetryingPolicies((prev) => new Set(prev).add(group.bulkUploadFileId));

      ToastSuccess(
        `Retrying PDF upload for ${failedCount} failed ${failedCount === 1 ? "policy" : "policies"
        }...`
      );

      // Call bulk retry API
      const response = await RetryBulkPdfUpload(group.bulkUploadFileId);

      if (response && !response.error) {
        ToastSuccess(response.message || "Successfully queued policies for retry");
        onRefresh();
      } else {
        ToastError("Failed to queue policies for retry");
      }
    } catch (error) {
      console.error("Error retrying PDF upload:", error);
      ToastError("Failed to retry PDF upload");
    } finally {
      // Remove from retrying set
      setRetryingPolicies((prev) => {
        const newSet = new Set(prev);
        newSet.delete(group.bulkUploadFileId);
        return newSet;
      });
    }
  };

  /**
   * Retry single failed policy
   */
  const handleRetrySinglePolicy = async (policyNumber) => {
    try {
      setRetryingPolicies((prev) => new Set(prev).add(policyNumber));
      const res = await RetryFailedPolicy(policyNumber);
      if (res && !res.error) {
        // Refresh data instead of page reload
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Error retrying policy:", error);
    } finally {
      setRetryingPolicies((prev) => {
        const newSet = new Set(prev);
        newSet.delete(policyNumber);
        return newSet;
      });
    }
  };

  /**
   * Handle screenshot preview
   */
  const handleOpenScreenshot = async (screenshotKey) => {
    try {
      // console.log("🖼️ Opening screenshot with key:", screenshotKey);

      // Use the existing downloadFileByKey service to get a signed URL
      const response = await downloadFileByKey(screenshotKey);

      // console.log("📥 downloadFileByKey response:", response);

      if (response && response.data && response.data.downloadURL) {
        // console.log("✅ Got signed URL:", response.data.downloadURL);
        // Use the signed URL directly
        setSelectedScreenshot(response.data.downloadURL);
        setScreenshotDialogOpen(true);
      } else {
        console.error("❌ No downloadURL in response:", response);
        ToastError("Failed to load screenshot");
      }
    } catch (error) {
      console.error("❌ Error loading screenshot:", error);
      ToastError("Failed to load screenshot");
    }
  };

  const handleCloseScreenshot = () => {
    setScreenshotDialogOpen(false);
    setSelectedScreenshot(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 350px)",
        position: "relative",
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflow: "auto",
          position: "relative",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#f9fafb !important",
                "& .MuiTableCell-root": {
                  backgroundColor: "#f9fafb !important",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#667085 !important",
                  fontFamily: "Inter",
                  borderBottom: "2px solid #DFE7F1",
                  padding: "4px 10px",
                  height: "50px",
                },
              }}
            >
              <TableCell>File Name</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "companyName"}
                  direction={sortBy === "companyName" ? sortOrder : "asc"}
                  onClick={() => onSortChange("companyName")}
                  sx={{
                    "&.MuiTableSortLabel-root": {
                      color: "#667085 !important",
                    },
                    "&.MuiTableSortLabel-root:hover": {
                      color: "#1E3A5F !important",
                    },
                    "&.Mui-active": {
                      color: "#1E3A5F !important",
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: "#1E3A5F !important",
                    },
                  }}
                >
                  Company Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Total Policies</TableCell>
              <TableCell>Matched</TableCell>
              <TableCell>Unmatched</TableCell>
              <TableCell>PDF Uploaded</TableCell>
              <TableCell>PDF Failed</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Completion Time</TableCell>
              <TableCell>Excel Files</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                  <CommonLoader />
                </TableCell>
              </TableRow>
            ) : bulkUploadedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No bulk uploaded policies found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              bulkUploadedData.map((group) => {
                const isMenuOpen =
                  selectedFileGroupId === group.bulkUploadFileId;

                const handleDownloadFile = async () => {
                  if (!group.fileKey) {
                    ToastError("File key not available");
                    return;
                  }
                  try {
                    const res = await downloadFileByKey(group.fileKey);
                    if (res?.data?.downloadURL) {
                      window.open(res.data.downloadURL, "_blank");
                    } else {
                      ToastError("Failed to get download URL");
                    }
                  } catch (error) {
                    console.error("Error downloading file:", error);
                    ToastError("Failed to download file");
                  }
                };

                return (
                  <React.Fragment key={group.bulkUploadFileId}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        <Link
                          component="button"
                          variant="body2"
                          onClick={handleDownloadFile}
                          sx={{
                            color: "#1E3A5F",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontSize: "12px",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {getOriginalFileName(
                            group.fileName,
                            group.originalFileName
                          )}
                        </Link>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        {group.companyName || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        {/* <Chip
                          label={group.totalPolicies || 0}
                          color="primary"
                          size="small"
                          sx={{ fontSize: "11px", height: "22px" }}
                        /> */}
                        {group.totalPolicies || 0}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        {/* <Chip
                          label={group.matchedCount || 0}
                          color="success"
                          size="small"
                          onClick={() =>
                            onToggleShowMatched(group.bulkUploadFileId)
                          }
                          sx={{
                            cursor: "pointer",
                            fontSize: "11px",
                            height: "22px",
                          }}
                        /> */}
                        <span
                          onClick={() =>
                            onToggleShowMatched(group.bulkUploadFileId)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {group.matchedCount || 0}
                        </span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        {/* <Chip
                          label={group.unmatchedCount || 0}
                          color="error"
                          size="small"
                          onClick={() =>
                            onToggleShowUnmatched(group.bulkUploadFileId)
                          }
                          sx={{
                            cursor: "pointer",
                            fontSize: "11px",
                            height: "22px",
                          }}
                        /> */}
                        <span
                          onClick={() =>
                            onToggleShowUnmatched(group.bulkUploadFileId)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {group.unmatchedCount || 0}
                        </span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        <Tooltip
                          title={`${group.pdfStats?.uploaded || 0
                            } PDFs uploaded successfully`}
                        >
                          {/* <Chip
                            label={group.pdfStats?.uploaded || 0}
                            color="success"
                            size="small"
                            icon={<CheckCircleIcon sx={{ fontSize: "14px" }} />}
                            sx={{
                              minWidth: 60,
                              fontSize: "11px",
                              height: "22px",
                            }}
                          /> */}
                          {group.pdfStats?.uploaded || 0}
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        <Tooltip
                          title={`${group.pdfStats?.failed || 0
                            } PDFs failed to upload`}
                        >
                          {/* <Chip
                            label={group.pdfStats?.failed || 0}
                            color="error"
                            size="small"
                            sx={{
                              minWidth: 60,
                              fontSize: "11px",
                              height: "22px",
                            }}
                          /> */}
                          {group.pdfStats?.failed || 0}
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        {moment(group.createdAt).format("DD/MM/YYYY HH:mm")}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        {(() => {
                          if (
                            group.processingTimeMinutes !== undefined &&
                            group.processingTimeMinutes !== null
                          ) {
                            const totalMinutes = parseFloat(
                              group.processingTimeMinutes
                            );
                            const minutes = Math.floor(totalMinutes);
                            const seconds = Math.round(
                              (totalMinutes - minutes) * 60
                            );
                            if (minutes === 0 && seconds === 0) {
                              return "-";
                            }
                            return `${minutes}m ${seconds}s`;
                          }
                          return "-";
                        })()}
                      </TableCell>
                      <TableCell>
                        {/* Excel Files Download */}
                        {group.correctionFiles &&
                          group.correctionFiles.length > 0 ? (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              endIcon={<ExpandMoreIcon />}
                              onClick={(e) =>
                                handleFilesMenuClick(e, group.bulkUploadFileId)
                              }
                              sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                borderColor: "#1E3A5F",
                                color: "#1E3A5F",
                                "&:hover": {
                                  borderColor: "#0056CC",
                                  backgroundColor: "#E6F2FF",
                                },
                              }}
                            >
                              {1 + group.correctionFiles.length} files
                            </Button>
                            <Menu
                              anchorEl={filesMenuAnchor}
                              open={isMenuOpen}
                              onClose={handleFilesMenuClose}
                              PaperProps={{
                                sx: {
                                  maxHeight: 300,
                                  width: "350px",
                                },
                              }}
                            >
                              {/* Original File */}
                              <MenuItem
                                onClick={() =>
                                  handleDownloadFileByKey(
                                    group.fileKey,
                                    group.fileName
                                  )
                                }
                                sx={{ py: 1.5 }}
                              >
                                <ListItemIcon>
                                  <InsertDriveFileIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                      >
                                        {getOriginalFileName(
                                          group.fileName,
                                          group.originalFileName
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Original •{" "}
                                        {moment(group.createdAt).format(
                                          "DD/MM/YYYY HH:mm"
                                        )}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </MenuItem>

                              {/* Correction Files */}
                              {group.correctionFiles.map((file, index) => (
                                <MenuItem
                                  key={file._id}
                                  onClick={() =>
                                    handleDownloadFileByKey(
                                      file.fileKey,
                                      file.fileName
                                    )
                                  }
                                  sx={{ py: 1.5 }}
                                >
                                  <ListItemIcon>
                                    <CheckCircleIcon
                                      fontSize="small"
                                      color="success"
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                        >
                                          {getOriginalFileName(
                                            file.fileName,
                                            file.originalFileName
                                          )}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Correction #{index + 1} •{" "}
                                          {moment(file.createdAt).format(
                                            "DD/MM/YYYY HH:mm"
                                          )}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </MenuItem>
                              ))}
                            </Menu>
                          </>
                        ) : (
                          <Tooltip title="Download original file">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleDownloadFile}
                              sx={{ padding: "4px" }}
                            >
                              <InsertDriveFileIcon sx={{ fontSize: "18px" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontFamily: "Inter",
                          color: "#667085",
                          padding: "8px 10px",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Download policies with missing fields">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  onDownloadMissingFields(
                                    group.bulkUploadFileId
                                  )
                                }
                                disabled={group.unmatchedCount === 0}
                                sx={{ padding: "4px" }}
                              >
                                <DownloadIcon sx={{ fontSize: "18px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Import and update policies">
                            <span>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => onImportClick(group)}
                                disabled={group.unmatchedCount === 0}
                              >
                                <UploadFileIcon sx={{ fontSize: "18px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              group.pdfStats?.failed > 0
                                ? `Retry PDF upload for ${group.pdfStats.failed
                                } failed ${group.pdfStats.failed === 1
                                  ? "policy"
                                  : "policies"
                                }`
                                : "No failed PDFs"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRetryPDFUpload(group)}
                                disabled={
                                  !group.pdfStats?.failed ||
                                  group.pdfStats.failed === 0 ||
                                  retryingPolicies.has(group.bulkUploadFileId)
                                }
                                sx={{
                                  color:
                                    group.pdfStats?.failed > 0
                                      ? "#3B82F6"
                                      : "#9CA3AF",
                                  "&:hover": {
                                    backgroundColor:
                                      group.pdfStats?.failed > 0
                                        ? "#DBEAFE"
                                        : "transparent",
                                  },
                                }}
                              >
                                {retryingPolicies.has(
                                  group.bulkUploadFileId
                                ) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <RefreshIcon sx={{ fontSize: "18px" }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => onExpandRow(group.bulkUploadFileId)}
                          >
                            {expandedRows[group.bulkUploadFileId] ? (
                              <ExpandLessIcon sx={{ fontSize: "18px" }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: "18px" }} />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={11}
                      >
                        <Collapse
                          in={expandedRows[group.bulkUploadFileId]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 2 }}>
                            {/* Matched Policies Table */}
                            {group.matched && group.matched.length > 0 && (
                              <Box
                                sx={{
                                  mb: 3,
                                  border: "1px solid #E0E0E0",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  backgroundColor: "#FFFFFF",
                                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "8px 16px",
                                    backgroundColor: "#F0F9FF",
                                    borderBottom: showMatched[
                                      group.bulkUploadFileId
                                    ]
                                      ? "1px solid #E0E0E0"
                                      : "none",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "#E0F2FE",
                                    },
                                  }}
                                  onClick={() =>
                                    onToggleShowMatched(group.bulkUploadFileId)
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <CheckCircleIcon
                                      sx={{
                                        color: "#10B981",
                                        fontSize: "24px",
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "16px",
                                          color: "#1F2937",
                                          fontFamily: "Inter",
                                        }}
                                      >
                                        Matched Policies
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#6B7280",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {group.matched.length}{" "}
                                        {group.matched.length === 1
                                          ? "policy"
                                          : "policies"}{" "}
                                        successfully matched
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Chip
                                      label={group.matched.length}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#10B981",
                                        color: "#FFFFFF",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      sx={{
                                        color: "#1E3A5F",
                                        transform: showMatched[
                                          group.bulkUploadFileId
                                        ]
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.3s ease",
                                      }}
                                    >
                                      <ExpandMoreIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Collapse
                                  in={showMatched[group.bulkUploadFileId]}
                                  timeout="auto"
                                >
                                  <TableContainer
                                    sx={{
                                      maxHeight: "400px",
                                      overflow: "auto",
                                      "&::-webkit-scrollbar": {
                                        width: "8px",
                                        height: "8px",
                                      },
                                      "&::-webkit-scrollbar-track": {
                                        background: "#f1f1f1",
                                      },
                                      "&::-webkit-scrollbar-thumb": {
                                        background: "#cbd5e1",
                                        borderRadius: "4px",
                                      },
                                      "&::-webkit-scrollbar-thumb:hover": {
                                        background: "#94a3b8",
                                      },
                                    }}
                                  >
                                    <Table
                                      size="small"
                                      stickyHeader
                                      sx={{ width: "100%" }}
                                    >
                                      <TableHead>
                                        <TableRow
                                          sx={{
                                            "& .MuiTableCell-root": {
                                              backgroundColor: "#F9FAFB",
                                              fontWeight: 500,
                                              fontSize: "11px",
                                              color: "#667085",
                                              borderBottom: "2px solid #DFE7F1",
                                              padding: "4px 10px",
                                              fontFamily: "Inter",
                                              height: "35px",
                                            },
                                          }}
                                        >
                                          <TableCell>Policy Number</TableCell>
                                          <TableCell>Customer Name</TableCell>
                                          <TableCell>
                                            Registration Number
                                          </TableCell>
                                          {/* <TableCell>Make</TableCell> */}
                                          <TableCell>Model</TableCell>
                                          <TableCell>Total Premium</TableCell>
                                          <TableCell align="center">
                                            Status
                                          </TableCell>
                                          <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {group.matched.map((policy, index) => (
                                          <TableRow
                                            key={policy._id}
                                            sx={{
                                              "&:hover": {
                                                backgroundColor: "#F9FAFB",
                                              },
                                              "&:last-child td": {
                                                borderBottom: 0,
                                              },
                                              "& .MuiTableCell-root": {
                                                padding: "8px 10px",
                                                fontSize: "12px",
                                                color: "#667085",
                                                borderBottom:
                                                  "1px solid #F3F4F6",
                                              },
                                            }}
                                          >
                                            <TableCell>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 500,
                                                  color: "#1F2937",
                                                }}
                                              >
                                                {policy.policyNumber}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              {policy.customerName}
                                            </TableCell>
                                            <TableCell>
                                              {policy.registrationNumber}
                                            </TableCell>
                                            {/* <TableCell>{policy.make}</TableCell> */}
                                            <TableCell>
                                              {policy.model}
                                            </TableCell>
                                            <TableCell>
                                              <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                ₹
                                                {policy.totalPremium
                                                  ? Number(
                                                    policy.totalPremium
                                                  ).toLocaleString("en-IN")
                                                  : "0"}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                              <Chip
                                                label={
                                                  policy.status ||
                                                  "entryPending"
                                                }
                                                size="small"
                                                sx={{
                                                  fontWeight: 500,
                                                  fontSize: "12px",
                                                  textTransform: "capitalize",
                                                }}
                                                color={
                                                  policy.status === "approved"
                                                    ? "success"
                                                    : policy.status ===
                                                      "rejected"
                                                      ? "error"
                                                      : "warning"
                                                }
                                              />
                                            </TableCell>
                                            {/* <TableCell align="center">
                                              <Chip
                                                label={policy.policyFile ? "Uploaded" : "Failed"}
                                                size="small"
                                                sx={{
                                                  fontWeight: 500,
                                                  fontSize: "12px",
                                                }}
                                                color={policy.policyFile ? "success" : "error"}
                                              />
                                            </TableCell> */}
                                            <TableCell align="center">
                                              {policy.pdfStatus === "failed" && (
                                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                  <Tooltip title="Retry PDF upload">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleRetrySinglePolicy(policy.policyNumber)}
                                                      disabled={retryingPolicies.has(policy.policyNumber)}
                                                      sx={{
                                                        color: "#3B82F6",
                                                        "&:hover": {
                                                          backgroundColor: "#DBEAFE",
                                                        },
                                                      }}
                                                    >
                                                      {retryingPolicies.has(policy.policyNumber) ? (
                                                        <CircularProgress size={18} color="inherit" />
                                                      ) : (
                                                        <RefreshIcon sx={{ fontSize: "18px" }} />
                                                      )}
                                                    </IconButton>
                                                  </Tooltip>
                                                  {policy.failedScreenshot && (
                                                    <Tooltip title="View error screenshot">
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenScreenshot(policy.failedScreenshot)}
                                                        sx={{
                                                          color: "#EF4444",
                                                          "&:hover": {
                                                            backgroundColor: "#FEE2E2",
                                                          },
                                                        }}
                                                      >
                                                        <ImageIcon sx={{ fontSize: "18px" }} />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                </Box>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Collapse>
                              </Box>
                            )}

                            {/* Unmatched Policies Table */}
                            {group.unmatched && group.unmatched.length > 0 && (
                              <Box
                                sx={{
                                  border: "1px solid #E0E0E0",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  backgroundColor: "#FFFFFF",
                                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "8px 16px",
                                    backgroundColor: "#FEF2F2",
                                    borderBottom: showUnmatched[
                                      group.bulkUploadFileId
                                    ]
                                      ? "1px solid #E0E0E0"
                                      : "none",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "#FEE2E2",
                                    },
                                  }}
                                  onClick={() =>
                                    onToggleShowUnmatched(
                                      group.bulkUploadFileId
                                    )
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                        backgroundColor: "#EF4444",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#FFFFFF",
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      !
                                    </Box>
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "16px",
                                          color: "#1F2937",
                                          fontFamily: "Inter",
                                        }}
                                      >
                                        Unmatched Policies
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#6B7280",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {group.unmatched.length}{" "}
                                        {group.unmatched.length === 1
                                          ? "policy"
                                          : "policies"}{" "}
                                        with missing or incomplete data
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Chip
                                      label={group.unmatched.length}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#EF4444",
                                        color: "#FFFFFF",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      sx={{
                                        color: "#1E3A5F",
                                        transform: showUnmatched[
                                          group.bulkUploadFileId
                                        ]
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.3s ease",
                                      }}
                                    >
                                      <ExpandMoreIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Collapse
                                  in={showUnmatched[group.bulkUploadFileId]}
                                  timeout="auto"
                                >
                                  <TableContainer
                                    sx={{
                                      maxHeight: "400px",
                                      overflow: "auto",
                                      "&::-webkit-scrollbar": {
                                        width: "8px",
                                        height: "8px",
                                      },
                                      "&::-webkit-scrollbar-track": {
                                        background: "#f1f1f1",
                                      },
                                      "&::-webkit-scrollbar-thumb": {
                                        background: "#cbd5e1",
                                        borderRadius: "4px",
                                      },
                                      "&::-webkit-scrollbar-thumb:hover": {
                                        background: "#94a3b8",
                                      },
                                    }}
                                  >
                                    <Table
                                      size="small"
                                      stickyHeader
                                      sx={{ width: "100%" }}
                                    >
                                      <TableHead>
                                        <TableRow
                                          sx={{
                                            "& .MuiTableCell-root": {
                                              backgroundColor: "#F9FAFB",
                                              fontWeight: 500,
                                              fontSize: "11px",
                                              color: "#667085",
                                              borderBottom: "2px solid #DFE7F1",
                                              padding: "4px 10px",
                                              fontFamily: "Inter",
                                              height: "35px",
                                            },
                                          }}
                                        >
                                          <TableCell>Policy Number</TableCell>
                                          <TableCell>Customer Name</TableCell>
                                          <TableCell>
                                            Registration Number
                                          </TableCell>
                                          {/* <TableCell>Make</TableCell> */}
                                          <TableCell>Model</TableCell>
                                          <TableCell>Total Premium</TableCell>
                                          <TableCell align="center">
                                            Missing Fields
                                          </TableCell>
                                          <TableCell align="center">
                                            Status
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {group.unmatched.map(
                                          (policy, index) => (
                                            <TableRow
                                              key={policy._id}
                                              sx={{
                                                "&:hover": {
                                                  backgroundColor: "#F9FAFB",
                                                },
                                                "&:last-child td": {
                                                  borderBottom: 0,
                                                },
                                                "& .MuiTableCell-root": {
                                                  padding: "8px 10px",
                                                  fontSize: "12px",
                                                  color: "#667085",
                                                  borderBottom:
                                                    "1px solid #F3F4F6",
                                                },
                                              }}
                                            >
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 500,
                                                    color: "#1F2937",
                                                  }}
                                                >
                                                  {policy.policyNumber}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                {policy.customerName || "-"}
                                              </TableCell>
                                              <TableCell>
                                                {policy.registrationNumber ||
                                                  "-"}
                                              </TableCell>
                                              {/* <TableCell>{policy.make || "-"}</TableCell> */}
                                              <TableCell>
                                                {policy.model || "-"}
                                              </TableCell>
                                              <TableCell>
                                                {policy.totalPremium ? (
                                                  <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500 }}
                                                  >
                                                    ₹
                                                    {Number(
                                                      policy.totalPremium
                                                    ).toLocaleString("en-IN")}
                                                  </Typography>
                                                ) : (
                                                  "-"
                                                )}
                                              </TableCell>
                                              <TableCell align="center">
                                                {(() => {
                                                  // Get company name and normalize it
                                                  const companyName =
                                                    group.companyName?.toLowerCase();

                                                  // Get company-specific required fields
                                                  const requiredFields =
                                                    COMPANY_REQUIRED_FIELDS[
                                                    companyName
                                                    ] || [];

                                                  // Filter missing fields to only show those that are required for this company
                                                  const relevantMissingFields =
                                                    policy.missingFields?.filter(
                                                      (field) =>
                                                        requiredFields.includes(
                                                          field
                                                        )
                                                    ) || [];

                                                  return relevantMissingFields.length ? (
                                                    <Tooltip
                                                      title={
                                                        <Box sx={{ p: 0.5 }}>
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              fontWeight: 600,
                                                              display: "block",
                                                              mb: 0.5,
                                                            }}
                                                          >
                                                            Missing Mandatory
                                                            Fields:
                                                          </Typography>
                                                          {relevantMissingFields.map(
                                                            (field, idx) => (
                                                              <Typography
                                                                key={idx}
                                                                variant="caption"
                                                                sx={{
                                                                  display:
                                                                    "block",
                                                                }}
                                                              >
                                                                • {field}
                                                              </Typography>
                                                            )
                                                          )}
                                                        </Box>
                                                      }
                                                      placement="top"
                                                      arrow
                                                    >
                                                      <Chip
                                                        label={`${relevantMissingFields.length} missing`}
                                                        size="small"
                                                        color="error"
                                                        sx={{
                                                          cursor: "help",
                                                          fontWeight: 500,
                                                          fontSize: "12px",
                                                        }}
                                                      />
                                                    </Tooltip>
                                                  ) : (
                                                    <Chip
                                                      label="Complete"
                                                      size="small"
                                                      color="success"
                                                      sx={{
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                      }}
                                                    />
                                                  );
                                                })()}
                                              </TableCell>
                                              <TableCell align="center">
                                                <Chip
                                                  label={
                                                    policy.status ||
                                                    "entryPending"
                                                  }
                                                  size="small"
                                                  sx={{
                                                    fontWeight: 500,
                                                    fontSize: "12px",
                                                    textTransform: "capitalize",
                                                  }}
                                                  color={
                                                    policy.status === "approved"
                                                      ? "success"
                                                      : policy.status ===
                                                        "rejected"
                                                        ? "error"
                                                        : "warning"
                                                  }
                                                />
                                              </TableCell>
                                            </TableRow>
                                          )
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Collapse>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Fixed Pagination Bar */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#f9fafb",
          borderTop: "1px solid #eaecf0",
          borderBottom: "1px solid #eaecf0",
          borderRadius: "0px 0px 10px 10px",
          zIndex: 10,
          padding: "8px 16px",
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{
            border: "none",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 0,
              paddingRight: 0,
              minHeight: "auto",
            },
            "& .MuiTablePagination-spacer": {
              flex: "none",
            },
            "& .MuiTablePagination-selectLabel": {
              margin: 0,
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      </Box>

      {/* Screenshot Preview Dialog */}
      <Dialog
        open={screenshotDialogOpen}
        onClose={handleCloseScreenshot}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2937" }}>
            Error Screenshot
          </Typography>
          <IconButton
            onClick={handleCloseScreenshot}
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": {
                backgroundColor: "#e5e7eb",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f3f4f6",
            minHeight: "400px",
          }}
        >
          {selectedScreenshot ? (
            <Box
              component="img"
              src={selectedScreenshot}
              alt="Failed screenshot"
              onError={(e) => {
                console.error("Failed to load screenshot:", selectedScreenshot);
                e.target.onerror = null;
                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999'%3EImage failed to load%3C/text%3E%3C/svg%3E";
              }}
              sx={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#fff",
              }}
            />
          ) : (
            <Typography color="text.secondary">No screenshot available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BulkUploadedPolicyTable;
